import { cn } from '@/lib/utils';
import { RadioGroup } from '@headlessui/react';
import { MagnifyingGlassIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { CheckCircleIcon, UserGroupIcon } from '@heroicons/react/24/solid';
import React, { useEffect, useRef, useState } from 'react';
import { hcVerbiage } from '../../common/verbiage';
import { PatientInfo } from '../../contexts/PatientPortalContext';

// Matches the sticky band's `top-20` (5rem = 80px). Kept in sync manually.
const STICKY_TOP_PX = 80;

type Props = {
  patients: PatientInfo[];
  onSelect: (patient: PatientInfo) => void;
  language?: 'en' | 'es';
};

const PatientSelect: React.FC<Props> = ({ patients, onSelect, language = 'en' }) => {
  const [search, setSearch] = useState('');
  // Tracks whether the sticky search band is currently pinned to the top.
  // Used to fade in the bottom gradient only when content is actually
  // scrolling behind the band (hiding it when the page is at rest).
  const [isStuck, setIsStuck] = useState(false);
  const sentinelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = sentinelRef.current;

    if (!el || typeof IntersectionObserver === 'undefined') return;

    // Shrink the observer root's top by the sticky's top offset. While the
    // sentinel is above that line (scrolled past), it stops intersecting —
    // which is our cue that the sticky band has engaged.
    const observer = new IntersectionObserver(([entry]) => setIsStuck(!entry.isIntersecting), {
      rootMargin: `-${STICKY_TOP_PX}px 0px 0px 0px`,
      threshold: 0,
    });

    observer.observe(el);

    return () => observer.disconnect();
  }, []);

  const filteredPatients = search.trim()
    ? patients.filter(p => {
        const term = search.trim().toLowerCase();

        return p.fname?.toLowerCase().includes(term) || p.lname?.toLowerCase().includes(term);
      })
    : patients;

  return (
    <div className="mx-auto max-w-lg py-8">
      <div className="text-center mb-8">
        <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-[var(--primary-color)] text-white">
          <UserGroupIcon className="h-7 w-7" />
        </div>
        <h2 className="mt-4 text-2xl font-bold text-gray-900 dark:text-gray-100">
          {hcVerbiage.selectPatient[language]}
        </h2>
        <p className="mt-2 text-sm text-gray-400 dark:text-gray-500">
          {hcVerbiage.selectPatientDescription[language]}
        </p>
      </div>

      <div ref={sentinelRef} aria-hidden="true" className="h-px" />
      <div
        className={cn(
          'sticky top-20 z-10 -mx-4 px-4 sm:-mx-6 sm:px-6 lg:-mx-8 lg:px-8 pt-6 pb-4 bg-[var(--color-bg-page)]',
          "after:content-[''] after:pointer-events-none after:absolute after:left-0 after:right-0 after:top-full after:h-6 after:bg-linear-to-b after:from-[var(--color-bg-page)] after:to-transparent after:opacity-0 after:transition-opacity after:duration-200",
          isStuck && 'after:opacity-100'
        )}
      >
        <div className="relative">
          <MagnifyingGlassIcon className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400 dark:text-gray-500" />
          <input
            type="text"
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search patients..."
            className="w-full rounded-xl border-0 bg-white dark:bg-gray-900 py-2.5 pl-10 pr-10 text-sm text-gray-900 dark:text-gray-100 placeholder-gray-400 shadow-sm ring-1 ring-gray-200 dark:ring-white/10 focus:ring-2 focus:ring-[var(--primary-color)] focus:outline-none"
          />
          {search && (
            <button
              type="button"
              onClick={() => setSearch('')}
              className="absolute right-3.5 top-1/2 -translate-y-1/2 text-gray-400 dark:text-gray-500 hover:text-gray-600"
              aria-label="Clear search"
            >
              <XMarkIcon className="h-4 w-4" />
            </button>
          )}
        </div>
        {patients.length > 0 && (
          <p className="mt-2 text-xs text-gray-400 dark:text-gray-500 px-1">
            {filteredPatients.length} of {patients.length} patient{patients.length !== 1 ? 's' : ''}
          </p>
        )}
      </div>

      <RadioGroup onChange={onSelect}>
        <div className="grid grid-cols-1 gap-y-3">
          {filteredPatients.map(patient => (
            <RadioGroup.Option
              key={patient.ID}
              value={patient}
              className={({ checked, active }) =>
                cn(
                  'portal-card relative flex cursor-pointer p-4 focus:outline-none transition-shadow',
                  checked ? 'ring-2 ring-[var(--primary-color)]' : '',
                  active ? 'ring-2 ring-[var(--primary-color)]' : ''
                )
              }
            >
              {({ checked }) => (
                <>
                  <span className="flex flex-1 items-center gap-4">
                    <span
                      className="inline-flex h-11 w-11 items-center justify-center rounded-xl text-white font-bold text-sm"
                      style={{ backgroundColor: 'var(--primary-color)' }}
                    >
                      {patient.fname?.charAt(0)?.toUpperCase()}
                      {patient.lname?.charAt(0)?.toUpperCase()}
                    </span>
                    <div className="flex flex-col">
                      <RadioGroup.Label
                        as="span"
                        className="text-sm font-semibold text-gray-900 dark:text-gray-100"
                      >
                        {patient.fname} {patient.lname}
                      </RadioGroup.Label>
                    </div>
                  </span>
                  <CheckCircleIcon
                    className={cn(checked ? 'h-5 w-5 text-[var(--primary-color)]' : 'hidden')}
                    aria-hidden="true"
                  />
                </>
              )}
            </RadioGroup.Option>
          ))}
        </div>
      </RadioGroup>
    </div>
  );
};

export default PatientSelect;
